/* Small pieces webmail uses everywhere. */
import type { ReactNode } from "react";
import { useBrand } from "../lib/brand";
import { cx } from "../lib/cx";
import { avatarFor } from "./format";

/** Hosterr's mark, or the agency's logo (light and dark) or name (milestone 6 branding). */
export function BrandMark({ label = "Mail", className }: { label?: string; className?: string }) {
  const b = useBrand();
  const word = <span className="text-base leading-none font-black text-[#545454] dark:text-cream-300">{label}</span>;
  if (!b.platform && (b.logoLight || b.logoDark)) {
    const light = b.logoLight ?? b.logoDark!;
    const dark = b.logoDark ?? b.logoLight!;
    return (
      <span className={cx("inline-flex items-center gap-2", className)}>
        <img src={light} alt={b.name} className="h-8 max-w-[160px] object-contain dark:hidden" />
        <img src={dark} alt={b.name} className="hidden h-8 max-w-[160px] object-contain dark:block" />
        {word}
      </span>
    );
  }
  if (!b.platform) {
    return (
      <span className={cx("inline-flex items-center gap-2", className)}>
        <span className="max-w-[200px] truncate text-base leading-none font-semibold text-fg">{b.name}</span>
        {word}
      </span>
    );
  }
  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <img src="/brand-mark.png" alt="Hosterr" className="h-8 w-8 dark:brightness-[1.6]" />
      {word}
    </span>
  );
}

/** The sign-in footer: the brand's webmail ("Hosterr webmail", "Whitehardt webmail"). */
export function BrandFooter() {
  const b = useBrand();
  return <footer className="shrink-0 border-t border-line bg-surface px-4 py-3 text-center text-[10px] text-faint">{b.name} webmail</footer>;
}

export function CenteredCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4">
      <div className="card w-full max-w-sm p-6 text-center">{children}</div>
    </div>
  );
}

/** A sender's initial on the panel's neutral gray (no color per sender: color means something here). Never a third-party avatar. */
export function SenderAvatar({ address, name, size = 32, className }: { address: string; name?: string | null; size?: number; className?: string }) {
  const a = avatarFor(address, name);
  return (
    <span
      aria-hidden="true"
      className={cx("hx-avatar inline-flex shrink-0 items-center justify-center rounded-full leading-none font-semibold select-none", className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)) }}
    >
      {a.initial}
    </span>
  );
}

/** You: your uploaded picture, or your initial. */
export function SelfAvatar({ address, name, url, size = 32 }: { address: string; name?: string | null; url: string | null; size?: number }) {
  if (url) return <img src={url} alt="" className="shrink-0 rounded-full object-cover ring-1 ring-line" style={{ width: size, height: size }} />;
  return <SenderAvatar address={address} name={name} size={size} />;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex min-w-5 items-center justify-center rounded border border-line-strong bg-surface px-1 font-mono text-[10.5px] text-dim">{children}</kbd>;
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("font-mono", className)}>{children}</span>;
}

/** A quiet section label (sentence case, as in the panel), for the folder list's groups. */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("px-2.5 pb-1 text-[12px] font-medium text-dim select-none", className)}>{children}</div>;
}

/**
 * Teaching empty state: a plain icon in a quiet circle, what this place is for, and the one thing that helps.
 * Used by the list and the reading pane, so every empty place says something useful.
 */
export function Empty({ icon, title, children, action }: { icon: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-xs flex-col items-center px-6 py-12 text-center">
      <span aria-hidden="true" className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-panel text-dim">
        {icon}
      </span>
      <p className="text-[14px] font-medium text-fg">{title}</p>
      {children ? <div className="mt-1 text-[13px] leading-relaxed text-dim">{children}</div> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
